import {
	useEffect,
	useRef,
	useState,
} from 'react';

import {
	useNavigate,
} from 'react-router-dom';

import {
	useSubmitCampaignMutation,
	useApproveCampaignMutation,
	useRejectCampaignMutation,
	useStartCampaignMutation,
	usePauseCampaignMutation,
	useResumeCampaignMutation,
	useCancelCampaignMutation,
	useRescheduleCampaignMutation,
	useResendCampaignMutation,
} from 'api/metaWhatsappApi';

import useAppToast from 'components/toast';

import campaignError from '../campaignErrors';

import {
	availableControls,
} from '../detail/controls';

import {
	campaignFromResponse,
	idOf,
	mergeAcknowledgement,
} from '../_lib/campaignState';

import {
	invokeAction,
	unwrapRequest,
	ambiguousActionError,
} from '../_lib/actionProtocol';

import {
	duplicateCampaign,
	deleteCampaign,
	readCampaignAfterDelete,
} from '../_lib/requests';

/**
 * Shared list/detail write path.
 *
 * Single-flight actions.
 * Local acknowledgement.
 * Explicit refresh.
 * Safe reconciliation after ambiguous DELETE.
 */
export default function useCampaignActions({
	refetch,
	fulfilledTimeStamp,
	onDeleted,
}) {
	const navigate =
		useNavigate();

	const notify =
		useAppToast();

	const [submit] =
		useSubmitCampaignMutation();

	const [approve] =
		useApproveCampaignMutation();

	const [reject] =
		useRejectCampaignMutation();

	const [start] =
		useStartCampaignMutation();

	const [pause] =
		usePauseCampaignMutation();

	const [resume] =
		useResumeCampaignMutation();

	const [cancel] =
		useCancelCampaignMutation();

	const [reschedule] =
		useRescheduleCampaignMutation();

	const [resend] =
		useResendCampaignMutation();

	const [
		dialog,
		setDialog,
	] = useState(null);

	const [
		busy,
		setBusy,
	] = useState(null);

	const [
		acknowledged,
		setAcknowledged,
	] = useState({});

	const [
		verifying,
		setVerifying,
	] = useState({});

	const [
		deleted,
		setDeleted,
	] = useState({});

	/**
	 * Infinity in verifying is deletion-specific.
	 *
	 * A normal list poll must not clear an uncertain DELETE. Only checking the
	 * individual campaign can establish whether deletion actually happened.
	 */
	const pendingDeletes =
		useRef(
			new Set(),
		);

	const lock =
		useRef(false);

	const mounted =
		useRef(true);

	useEffect(() => {
		mounted.current =
			true;

		return () => {
			mounted.current =
				false;
		};
	}, []);

	useEffect(() => {
		if (
			!fulfilledTimeStamp
		) {
			return;
		}

		setVerifying(
			(previous) =>
				Object.fromEntries(
					Object.entries(
						previous,
					).filter(
						([
							,
							since,
						]) =>
							fulfilledTimeStamp <
							since,
					),
				),
		);
	}, [
		fulfilledTimeStamp,
	]);

	const effective = (
		campaign,
	) =>
		mergeAcknowledgement(
			campaign,
			acknowledged[
				idOf(campaign)
			],
		);

	const refresh =
		async () => {
			try {
				const result =
					await unwrapRequest(
						refetch(),
					);

				if (
					result?.error
				) {
					return false;
				}

				return true;
			} catch (_) {
				return false;
			}
		};

	const clearDeleteCheck = (
		id,
	) => {
		pendingDeletes.current.delete(
			id,
		);

		if (
			mounted.current
		) {
			setVerifying(
				(previous) => {
					const next = {
						...previous,
					};

					delete next[id];

					return next;
				},
			);
		}
	};

	const rememberDeleted = (
		id,
	) => {
		clearDeleteCheck(id);

		if (
			mounted.current
		) {
			setDeleted(
				(previous) => ({
					...previous,
					[id]: true,
				}),
			);

			onDeleted?.(id);
		}
	};

	const verifyDelete =
		async (id) => {
			const status =
				await readCampaignAfterDelete(
					id,
				);

			if (
				status.missing
			) {
				rememberDeleted(
					id,
				);
			} else {
				clearDeleteCheck(
					id,
				);

				if (
					mounted.current
				) {
					setAcknowledged(
						(previous) => ({
							...previous,
							[id]:
								status.campaign,
						}),
					);
				}
			}

			return status.missing;
		};

	const refreshAfterDelete =
		async () => {
			/**
			 * Detail page navigates away after DELETE.
			 *
			 * Do not refetch a detail endpoint which should now return 404.
			 */
			if (
				!onDeleted &&
				!(await refresh())
			) {
				notify.error(
					'List refresh delayed',
					'Deletion was confirmed. The removed campaign will remain hidden while the list reconnects.',
				);
			}
		};

	const recheck =
		async (campaign) => {
			const id =
				idOf(campaign);

			if (
				pendingDeletes.current.has(
					id,
				)
			) {
				try {
					const missing =
						await verifyDelete(
							id,
						);

					if (
						missing
					) {
						notify.success(
							'Campaign no longer available',
							'It has been removed from the campaign list.',
						);
					} else {
						notify.success(
							'Campaign status confirmed',
							'The campaign is still available. Its current actions have been refreshed.',
						);
					}

					await refreshAfterDelete();

					return true;
				} catch (_) {
					notify.error(
						'Deletion is not yet confirmed',
						'Check your connection and use Check status before trying another action.',
					);

					return false;
				}
			}

			const ok =
				await refresh();

			if (
				ok &&
				mounted.current
			) {
				setVerifying(
					(previous) => {
						const next = {
							...previous,
						};

						delete next[
							idOf(
								campaign,
							)
						];

						return next;
					},
				);
			}

			return ok;
		};

	const performDelete =
		async (
			id,
			payload,
		) => {
			try {
				await deleteCampaign({
					id,
					reason:
						payload.reason,
				});

				rememberDeleted(
					id,
				);

				notify.success(
					'Campaign deleted',
					'Removed from the campaign list. Audit history and CRM contacts are retained.',
				);

				await refreshAfterDelete();

				return true;
			} catch (error) {
				/**
				 * Network interruption means we do not know whether DELETE committed.
				 */
				if (
					!ambiguousActionError(
						error,
					) &&
					!(
						Number(
							error?.status,
						) ===
							404 &&
						error?.data
							?.error ===
							'CAMPAIGN_NOT_FOUND'
					)
				) {
					throw error;
				}

				pendingDeletes.current.add(
					id,
				);

				if (
					mounted.current
				) {
					setVerifying(
						(previous) => ({
							...previous,
							[id]:
								Infinity,
						}),
					);
				}

				try {
					const missing =
						await verifyDelete(
							id,
						);

					if (
						missing
					) {
						notify.success(
							'Campaign no longer available',
							'It has been removed from the campaign list.',
						);

						await refreshAfterDelete();

						return true;
					}

					await refresh();

					notify.error(
						'Deletion was not confirmed',
						'The campaign is still available. Review its current status before trying again.',
					);
				} catch (_) {
					notify.error(
						'Checking deletion status',
						'The response was interrupted. Use Check status before trying another action.',
					);
				}

				return false;
			}
		};

	const perform =
		async (
			action,
			campaign,
			payload = {},
		) => {
			const current =
				effective(
					campaign,
				);

			const id =
				idOf(
					current,
				);

			if (
				lock.current ||
				verifying[id] ||
				pendingDeletes.current.has(
					id,
				) ||
				deleted[id] ||
				!availableControls(
					current,
				).includes(
					action,
				)
			) {
				return false;
			}

			lock.current =
				true;

			setBusy({
				id,
				action,
			});

			try {
				if (
					action ===
					'delete'
				) {
					return await performDelete(
						id,
						payload,
					);
				}

				const result =
					await unwrapRequest(
						invokeAction(
							action,
							id,
							payload,
							{
								submit,
								approve,
								reject,
								start,
								pause,
								resume,
								cancel,
								reschedule,
								resend,
								duplicate:
									duplicateCampaign,
							},
						),
					);

				const updated =
					campaignFromResponse(
						result,
					);

				if (
					updated &&
					idOf(updated) ===
						id &&
					mounted.current
				) {
					setAcknowledged(
						(previous) => ({
							...previous,
							[id]: {
								...current,
								...updated,
							},
						}),
					);
				}

				if (
					[
						'duplicate',
						'resend',
					].includes(
						action,
					)
				) {
					if (
						!updated ||
						idOf(
							updated,
						) === id
					) {
						throw new Error(
							'A new draft was not returned. Refresh the list before trying again.',
						);
					}

					notify.success(
						'New draft created',
						'Review the audience, message and schedule. Nothing is launched automatically.',
					);

					navigate(
						`/meta-campaigns/${idOf(updated)}/edit`,
					);
				} else if (action === 'start') {
	notify.success(
		updated?.state === 'RUNNING'
			? 'Campaign is active'
			: 'Launch accepted',

		updated?.state === 'RUNNING'
			? 'Messages will be released when due.'
			: 'Preparing recipients and delivery times. You can leave this page.',
	);

	/**
	 * IMPORTANT:
	 *
	 * Launch is asynchronous.
	 *
	 * The POST /start response already contains the authoritative
	 * PLANNING/RUNNING state.
	 *
	 * Do not keep the confirmation modal open while waiting for another GET.
	 */
	if (mounted.current) {
		setDialog(null);
	}

	/**
	 * Revalidate in the background.
	 *
	 * Realtime/polling will later move:
	 *
	 * PLANNING -> RUNNING
	 * or
	 * PLANNING -> FAILED_TO_PLAN
	 *
	 * But none of that should block the Launch button/modal.
	 */
	void refresh().then((ok) => {
		if (
			!ok &&
			mounted.current
		) {
			notify.error(
				'Status refresh delayed',
				'Launch was accepted. Showing the confirmed launch state while live status reconnects.',
			);
		}
	});

	/**
	 * Returning immediately allows ConfirmDialog to clear its local spinner.
	 */
	return true;
} else if (action === 'reschedule') {
					notify.success(
						'Delivery schedule updated',
						`${Number(result?.moved) || 0} moved; ${Number(result?.skipped) || 0} could not fit the new window.`,
					);
				} else {
					notify.success(
						{
							submit:
								'Campaign submitted',
							approve:
								'Campaign approved',
							reject:
								'Returned to draft',
							pause:
								'Campaign paused',
							resume:
								'Campaign resumed',
							cancel:
								'Campaign cancelled',
						}[action] ??
							'Campaign updated',

						result?.note,
					);
				}

				if (
					!(await refresh())
				) {
					notify.error(
						'Status refresh failed',
						'The action was accepted. Showing the last confirmed state while we reconnect.',
					);
				}

				return true;
			} catch (error) {
				if (
					ambiguousActionError(
						error,
					) &&
					mounted.current
				) {
					setVerifying(
						(previous) => ({
							...previous,
							[id]:
								Date.now(),
						}),
					);
				}

				const confirmed =
					await refresh();

				if (
					confirmed &&
					mounted.current
				) {
					setVerifying(
						(previous) => {
							const next = {
								...previous,
							};

							delete next[
								id
							];

							return next;
						},
					);
				}

				notify.error(
					ambiguousActionError(
						error,
					)
						? 'Checking the campaign status'
						: 'Action could not be completed',

					ambiguousActionError(
						error,
					)
						? 'The response was interrupted. Do not launch again until the current status is confirmed.'
						: campaignError(
								error,
							),
				);

				return false;
			} finally {
				lock.current =
					false;

				if (
					mounted.current
				) {
					setBusy(null);
				}
			}
		};

	const request = (
		action,
		campaign,
	) => {
		const current =
			effective(
				campaign,
			);

		const id =
			idOf(
				current,
			);

		if (
			deleted[id] ||
			current?.deletedAt
		) {
			return;
		}

		if (
			action === 'view'
		) {
			navigate(
				`/meta-campaigns/${id}`,
			);

			return;
		}

		if (
			lock.current ||
			busy ||
			verifying[id] ||
			pendingDeletes.current.has(
				id,
			) ||
			!availableControls(
				current,
			).includes(
				action,
			)
		) {
			return;
		}

		if (
			action === 'edit'
		) {
			navigate(
				`/meta-campaigns/${id}/edit`,
			);

			return;
		}

		setDialog({
			action,
			campaign: current,
		});
	};

	return {
		request,
		perform,
		effective,
		busy,
		dialog,

		closeDialog: () => {
			if (
				!lock.current
			) {
				setDialog(null);
			}
		},

		verifying,
		recheck,
		deleted,

		isBusy:
			Boolean(busy),
	};
}